import { configureStore, combineReducers, Middleware } from "@reduxjs/toolkit";
import createSagaMiddleware from "redux-saga";
import logger from "redux-logger";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  PersistConfig,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { useDispatch, useSelector, TypedUseSelectorHook } from "react-redux";
import { migrateFromLegacyState } from "../utils/persiststate";
import sagas from "./saga";
import * as Sentry from '@sentry/react';

import accountingReducer from "./slices/accounting";
import accountsReducer from "./slices/accounts";
import addOnsReducer from "./slices/addOns";
import brandsReducer from "./slices/brands";
import bulkUploadReduer from "./slices/bulk-upload";
import businessReducer from "./slices/business";
import catalogReducer from "./slices/catalog";
import channelMenuReducer from "./slices/channel-menu";
import chargesAndDiscountsReducer from "./slices/chargesAndDiscounts";
import dashboardReducer from "./slices/dashboard";
import galleryReducer from "./slices/gallery";
import itemSchedulesReducer from "./slices/itemSchedules";
import loginReducer from "./slices/login";
import menusReducer from "./slices/menus";
import outletStatusReducer from "./slices/outlet-status";
import pnlStatementReducer from "./slices/pnl-statement";
import reportsReducer from "./slices/reports";
import requestHeadersReducer from "./slices/request-headers";
import savedReportReducer from "./slices/downloads";
import taxesReducer from "./slices/taxes";
import commonReducer from "./slices/common";
import tablesReducer from "./slices/tables";
import inventoryReducer from "./slices/inventory";
import recommenderReducer from "./slices/recommender";
import ristaCardReducer from "./slices/ristaCard";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
import { isProdEnv } from "../data/env-constants";

// Migrate data stored by the old manual loadState/saveState approach into
// the redux-persist storage format before the store is created.
migrateFromLegacyState();

const rootReducer = combineReducers({
  accountingReducer,
  accountsReducer,
  addOnsReducer,
  brandsReducer,
  bulkUploadReduer,
  businessReducer,
  catalogReducer,
  channelMenuReducer,
  chargesAndDiscountsReducer,
  dashboardReducer,
  galleryReducer,
  itemSchedulesReducer,
  loginReducer,
  menusReducer,
  outletStatusReducer,
  pnlStatementReducer,
  reportsReducer,
  requestHeadersReducer,
  savedReportReducer,
  taxesReducer,
  commonReducer,
  tablesReducer,
  inventoryReducer,
  recommenderReducer,
  ristaCardReducer,
});

type RootReducerType = ReturnType<typeof rootReducer>;

const persistConfig: PersistConfig<RootReducerType> = {
  key: "root",
  storage,
  version: 1,
  stateReconciler: autoMergeLevel2,
  throttle: 1000,
  // ristaCardReducer was not persisted in the previous implementation
  blacklist: ["ristaCardReducer"],
};

const persistedReducer = persistReducer<RootReducerType>(
  persistConfig,
  rootReducer as any
);

const sagaMiddleware = createSagaMiddleware();
const middlewares: Middleware[] = [sagaMiddleware];

if (!isProdEnv) {
  middlewares.push(logger);
}
const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Optionally pass options listed below
  attachReduxState: false,
});
const store: any = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: false,
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
    .concat(...middlewares),
  enhancers: (getDefaultEnhancers) => {
    return getDefaultEnhancers().concat(sentryReduxEnhancer);
  },
  
});

export const persistor = persistStore(store);

let sagaStarted = false;

persistor.subscribe(() => {
  const { bootstrapped } = persistor.getState();

  if (bootstrapped && !sagaStarted) {
    sagaStarted = true;
    sagaMiddleware.run(sagas);
  }
});


export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export default store;
